import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
	CreditCard,
	Search,
	Filter,
	Building2,
	ChevronLeft,
	ChevronRight,
	ExternalLink,
	Crown,
	Clock,
	AlertTriangle,
	XCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import * as api from "@/services/api";

const planColors: Record<string, string> = {
	free: "bg-gray-100 text-gray-700",
	starter: "bg-blue-100 text-blue-700",
	professional: "bg-purple-100 text-purple-700",
	enterprise: "bg-amber-100 text-amber-700",
};

const statusColors: Record<string, string> = {
	active: "bg-green-100 text-green-700",
	inactive: "bg-gray-100 text-gray-700",
	cancelled: "bg-red-100 text-red-700",
	trialing: "bg-blue-100 text-blue-700",
	past_due: "bg-yellow-100 text-yellow-700",
	incomplete: "bg-orange-100 text-orange-700",
};

const plans = ["free", "starter", "professional", "enterprise"];
const statuses = [
	"active",
	"inactive",
	"cancelled",
	"trialing",
	"past_due",
	"incomplete",
];

function formatStatus(status: string) {
	return status
		.split("_")
		.map((s) => s.charAt(0).toUpperCase() + s.slice(1))
		.join(" ");
}

export function SubscriptionList() {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");
	const [selectedPlan, setSelectedPlan] = useState<string>("");
	const [selectedStatus, setSelectedStatus] = useState<string>("");
	const [showFilters, setShowFilters] = useState(false);

	const { data, isLoading, error } = useQuery({
		queryKey: [
			"admin-subscriptions",
			page,
			search,
			selectedPlan,
			selectedStatus,
		],
		queryFn: () =>
			api.getAdminOrganizations({
				page,
				limit: 20,
				search: search || undefined,
				plan: selectedPlan || undefined,
				subscriptionStatus: selectedStatus || undefined,
				sortBy: "createdAt",
				sortOrder: "desc",
			}),
	});

	const { data: overviewStats } = useQuery({
		queryKey: ["admin-organizations-overview"],
		queryFn: api.getOrganizationsOverview,
	});

	const handleSearch = (e: React.FormEvent) => {
		e.preventDefault();
		setSearch(searchInput);
		setPage(1);
	};

	const clearFilters = () => {
		setSearch("");
		setSearchInput("");
		setSelectedPlan("");
		setSelectedStatus("");
		setPage(1);
	};

	const hasFilters = search || selectedPlan || selectedStatus;

	const stats = overviewStats?.organizations;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-2xl font-bold text-gray-900">Subscriptions</h1>
				<p className="text-gray-500 mt-1">
					Manage organization subscriptions and billing
				</p>
			</div>

			{/* Stats Cards */}
			{stats && (
				<div className="grid grid-cols-2 md:grid-cols-5 gap-4">
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-blue-100 rounded-lg">
									<CreditCard className="w-5 h-5 text-blue-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">{stats.total}</p>
									<p className="text-sm text-gray-500">Total</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-green-100 rounded-lg">
									<Crown className="w-5 h-5 text-green-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{stats.byStatus?.active || 0}
									</p>
									<p className="text-sm text-gray-500">Active</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-blue-100 rounded-lg">
									<Clock className="w-5 h-5 text-blue-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{stats.byStatus?.trialing || 0}
									</p>
									<p className="text-sm text-gray-500">Trialing</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-yellow-100 rounded-lg">
									<AlertTriangle className="w-5 h-5 text-yellow-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{stats.byStatus?.past_due || 0}
									</p>
									<p className="text-sm text-gray-500">Past Due</p>
								</div>
							</div>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="p-4">
							<div className="flex items-center gap-3">
								<div className="p-2 bg-red-100 rounded-lg">
									<XCircle className="w-5 h-5 text-red-600" />
								</div>
								<div>
									<p className="text-2xl font-bold">
										{stats.byStatus?.cancelled || 0}
									</p>
									<p className="text-sm text-gray-500">Cancelled</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Search and Filters */}
			<Card>
				<CardContent className="p-4">
					<div className="flex flex-col md:flex-row gap-4">
						<form onSubmit={handleSearch} className="flex-1 flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<Input
									placeholder="Search organizations..."
									value={searchInput}
									onChange={(e) => setSearchInput(e.target.value)}
									className="pl-10"
								/>
							</div>
							<Button type="submit" variant="outline">
								Search
							</Button>
						</form>
						<Button
							variant="outline"
							onClick={() => setShowFilters(!showFilters)}
							className={cn(showFilters && "bg-gray-100")}
						>
							<Filter className="w-4 h-4 mr-2" />
							Filters
							{hasFilters && (
								<span className="ml-2 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-full">
									{[search, selectedPlan, selectedStatus].filter(Boolean).length}
								</span>
							)}
						</Button>
					</div>

					{showFilters && (
						<div className="mt-4 pt-4 border-t flex flex-wrap gap-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Plan
								</label>
								<select
									value={selectedPlan}
									onChange={(e) => {
										setSelectedPlan(e.target.value);
										setPage(1);
									}}
									className="px-3 py-2 border rounded-md text-sm"
								>
									<option value="">All Plans</option>
									{plans.map((plan) => (
										<option key={plan} value={plan}>
											{plan.charAt(0).toUpperCase() + plan.slice(1)}
										</option>
									))}
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-1">
									Status
								</label>
								<select
									value={selectedStatus}
									onChange={(e) => {
										setSelectedStatus(e.target.value);
										setPage(1);
									}}
									className="px-3 py-2 border rounded-md text-sm"
								>
									<option value="">All Statuses</option>
									{statuses.map((status) => (
										<option key={status} value={status}>
											{formatStatus(status)}
										</option>
									))}
								</select>
							</div>
							{hasFilters && (
								<div className="flex items-end">
									<Button variant="ghost" size="sm" onClick={clearFilters}>
										Clear Filters
									</Button>
								</div>
							)}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Subscriptions Table */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span>Subscriptions</span>
						{data && (
							<span className="text-sm font-normal text-gray-500">
								{data.pagination.total} total
							</span>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
						</div>
					) : error ? (
						<div className="text-center py-12 text-red-500">
							Failed to load subscriptions
						</div>
					) : data?.organizations.length === 0 ? (
						<div className="text-center py-12 text-gray-500">
							No subscriptions found
						</div>
					) : (
						<>
							<div className="overflow-x-auto">
								<table className="w-full">
									<thead>
										<tr className="border-b">
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Organization
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Plan
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Status
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Period End
											</th>
											<th className="text-left py-3 px-4 font-medium text-gray-500 text-sm">
												Created
											</th>
											<th className="text-right py-3 px-4 font-medium text-gray-500 text-sm">
												Actions
											</th>
										</tr>
									</thead>
									<tbody>
										{data?.organizations.map((org) => (
											<tr
												key={org._id}
												className="border-b last:border-0 hover:bg-gray-50"
											>
												<td className="py-3 px-4">
													<div className="flex items-center gap-3">
														<div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
															<Building2 className="w-5 h-5 text-gray-500" />
														</div>
														<div>
															<p className="font-medium text-gray-900">
																{org.name}
															</p>
															<p className="text-sm text-gray-500">
																{org.slug}
															</p>
														</div>
													</div>
												</td>
												<td className="py-3 px-4">
													<Badge className={planColors[org.plan] || ""}>
														{org.plan}
													</Badge>
												</td>
												<td className="py-3 px-4">
													<Badge
														className={
															statusColors[
																org.subscriptionStatus || "inactive"
															] || ""
														}
													>
														{formatStatus(
															org.subscriptionStatus || "inactive",
														)}
													</Badge>
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{org.subscriptionCurrentPeriodEnd
														? new Date(
																org.subscriptionCurrentPeriodEnd,
															).toLocaleDateString()
														: "-"}
												</td>
												<td className="py-3 px-4 text-sm text-gray-500">
													{org.createdAt
														? new Date(org.createdAt).toLocaleDateString()
														: "-"}
												</td>
												<td className="py-3 px-4 text-right">
													<Link to={`/organizations/${org._id}`}>
														<Button variant="ghost" size="sm">
															<ExternalLink className="w-4 h-4 mr-1" />
															View
														</Button>
													</Link>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							{data && data.pagination.pages > 1 && (
								<div className="flex items-center justify-between mt-4 pt-4 border-t">
									<p className="text-sm text-gray-500">
										Page {data.pagination.page} of {data.pagination.pages}
									</p>
									<div className="flex gap-2">
										<Button
											variant="outline"
											size="sm"
											disabled={page <= 1}
											onClick={() => setPage((p) => p - 1)}
										>
											<ChevronLeft className="w-4 h-4 mr-1" />
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											disabled={page >= data.pagination.pages}
											onClick={() => setPage((p) => p + 1)}
										>
											Next
											<ChevronRight className="w-4 h-4 ml-1" />
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
